import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { PageResponse } from '../../../../shared/commons/models/page-response';
import { firstPage, PageRequest, toPageParams } from '../../../../shared/commons/models/page';
import { AcademicYearResponse, CreateAcademicYearRequest } from '../models/school-academic-year.models';
import { CountryLevelTemplateResponse } from '../models/school-catalog.models';
import { SchoolLevelResponse } from '../models/school-level.models';
import { ApplyCountryTemplateRequest } from '../models/school-template.requests';
import { ProvisionedSchoolResponse, ProvisionSchoolRequest } from '../models/school-provision.models';
import { SchoolReadinessResponse } from '../models/school-readiness.models';
import { SchoolDetailResponse, SchoolSummaryResponse } from '../models/school.models';
import { SchoolWorkspace } from '../../../auth/models/authenticated.user';
import { AddGradeFeeRequest } from '../models/school-fee.models';
import { GradeFeeResponse } from '../models/school-readiness.models';
import { SchoolStatus } from '../models/school-types.models';

/**
 * One method per backend endpoint and nothing else — no state, no caching, no
 * error handling. `SchoolOnboardingStore` owns all of that; keeping this layer
 * dumb means an endpoint change is a one-line change here.
 *
 * <p>Cookies and the CSRF header are added by `CsrfInterceptor`.</p>
 */
@Injectable({ providedIn: 'root' })
export class SchoolAdminService {
  private http = inject(HttpClient);

  private adminUrl = `${environment.apiUrl}/v1/admin`;
  private schoolsUrl = `${environment.apiUrl}/v1/schools`;

  list(
    request: PageRequest = firstPage(),
    filters: { status?: SchoolStatus; query?: string } = {},
  ): Observable<PageResponse<SchoolSummaryResponse>> {
    let params = toPageParams(request);
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.query?.trim()) {
      params = params.set('query', filters.query.trim());
    }
    return this.http.get<PageResponse<SchoolSummaryResponse>>(`${this.adminUrl}/schools`, {
      params,
    });
  }

  get(schoolPublicId: string): Observable<SchoolDetailResponse> {
    return this.http.get<SchoolDetailResponse>(`${this.adminUrl}/schools/${schoolPublicId}`);
  }

  /** Creates the school and its first director in one transaction. */
  provision(request: ProvisionSchoolRequest): Observable<ProvisionedSchoolResponse> {
    return this.http.post<ProvisionedSchoolResponse>(`${this.adminUrl}/schools`, request);
  }

  countryTemplates(iso3CountryCode: string): Observable<CountryLevelTemplateResponse[]> {
    return this.http.get<CountryLevelTemplateResponse[]>(`${this.adminUrl}/country-templates`, {
      params: new HttpParams().set('iso3CountryCode', iso3CountryCode),
    });
  }

  /** Idempotent: re-applying the same level codes does not duplicate anything. */
  applyTemplate(schoolPublicId: string, request: ApplyCountryTemplateRequest): Observable<SchoolLevelResponse[]> {
    return this.http.post<SchoolLevelResponse[]>(`${this.adminUrl}/schools/${schoolPublicId}/apply-template`, request);
  }

  createAcademicYear(schoolPublicId: string, request: CreateAcademicYearRequest): Observable<AcademicYearResponse> {
    return this.http.post<AcademicYearResponse>(`${this.adminUrl}/schools/${schoolPublicId}/academic-years`, request);
  }

  readiness(schoolPublicId: string): Observable<SchoolReadinessResponse> {
    return this.http.get<SchoolReadinessResponse>(`${this.schoolsUrl}/${schoolPublicId}/readiness`);
  }

  levels(schoolPublicId: string): Observable<SchoolLevelResponse[]> {
    return this.http.get<SchoolLevelResponse[]>(`${this.schoolsUrl}/${schoolPublicId}/levels`);
  }

  academicYears(schoolPublicId: string): Observable<AcademicYearResponse[]> {
    return this.http.get<AcademicYearResponse[]>(`${this.schoolsUrl}/${schoolPublicId}/academic-years`);
  }

  workspace(schoolPublicId: string): Observable<SchoolWorkspace> {
    return this.http.get<SchoolWorkspace>(`${this.schoolsUrl}/${schoolPublicId}/workspace`);
  }

  fees(schoolPublicId: string, academicYearPublicId: string): Observable<GradeFeeResponse[]> {
    return this.http.get<GradeFeeResponse[]>(
      `${this.schoolsUrl}/${schoolPublicId}/academic-years/${academicYearPublicId}/fees`,
    );
  }

  addFees(
    schoolPublicId: string,
    academicYearPublicId: string,
    requests: AddGradeFeeRequest[],
  ): Observable<GradeFeeResponse[]> {
    return this.http.post<GradeFeeResponse[]>(
      `${this.schoolsUrl}/${schoolPublicId}/academic-years/${academicYearPublicId}/fees`,
      requests,
    );
  }

  activate(schoolPublicId: string): Observable<SchoolReadinessResponse> {
    return this.http.post<SchoolReadinessResponse>(`${this.schoolsUrl}/${schoolPublicId}/activate`, {});
  }
}
